import { useCallback, useEffect, useRef, useState } from "react";

export interface DebugLogEntry {
    entry_id: string;
    sequence: number;
    step_type: string;
    step_name: string;
    status: string;
    decision_summary: string;
    duration_ms: number;
    container_id: string | null;
    document_id: string | null;
    email_message_id?: string | null;
    change_detected?: boolean;
    type?: string;
}

interface Progress {
    emails_processed: number;
    emails_total: number;
    current_email_subject: string;
}

interface Summary {
    run_id: string;
    status: string;
    planned_changes_count: number;
    errors: number;
}

export interface StepInspectorEvent {
    event: string;
    step_name?: string;
    phase?: string;
    group?: string;           // SSE sends "group" instead of "phase"
    status?: string;
    duration_ms?: number;
    input_snapshot?: Record<string, unknown>;
    output_snapshot?: Record<string, unknown>;
    input?: Record<string, unknown>;    // SSE sends "input" instead of "input_snapshot"
    output?: Record<string, unknown>;   // SSE sends "output" instead of "output_snapshot"
    error?: string;
    traceback?: string;
    results?: Record<string, unknown>;
    /** run_complete payload from backend */
    total?: number;
    completed?: number;
    failed?: number;
}

interface UseDebugStreamResult {
    logs: DebugLogEntry[];
    progress: Progress | null;
    summary: Summary | null;
    isRunning: boolean;
    connect: (runId: string, token: string) => void;
    connectStepInspector: (emailId: string, token: string, body: object) => void;
    stepEvents: StepInspectorEvent[];
    clearStepEvents: () => void;
    disconnect: () => void;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
const API_PREFIX = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";

export function useDebugStream(): UseDebugStreamResult {
    const [logs, setLogs] = useState<DebugLogEntry[]>([]);
    const [progress, setProgress] = useState<Progress | null>(null);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [stepEvents, setStepEvents] = useState<StepInspectorEvent[]>([]);
    const eventSourceRef = useRef<EventSource | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const connect = useCallback((runId: string, token: string) => {
        // Close any existing connection
        eventSourceRef.current?.close();

        setIsRunning(true);
        setLogs([]);
        setProgress(null);
        setSummary(null);

        const base = API_BASE.replace(/\/$/, "");
        const prefix = API_PREFIX.startsWith("/") ? API_PREFIX : `/${API_PREFIX}`;
        const es = new EventSource(
            `${base}${prefix}/admin/debug/reprocess/${runId}/stream?token=${encodeURIComponent(token)}`
        );
        eventSourceRef.current = es;

        es.addEventListener("step", (e) => {
            setLogs((prev) => [...prev, JSON.parse((e as MessageEvent).data)]);
        });

        es.addEventListener("progress", (e) => {
            setProgress(JSON.parse((e as MessageEvent).data));
        });

        es.addEventListener("change", (e) => {
            const change = JSON.parse((e as MessageEvent).data);
            setLogs((prev) => [
                ...prev,
                {
                    // Spread ALL backend fields so formatLogLine can access them
                    ...change,
                    // Override required DebugLogEntry fields
                    step_type: "change",
                    status: "warning",
                    sequence: prev.length + 1,
                    entry_id: change.journal_entry_id ?? "",
                    step_name: change.type ?? "change",
                    decision_summary: "",
                    duration_ms: 0,
                    container_id: change.container_id ?? null,
                    document_id: change.source_document_id ?? change.document_id ?? null,
                    change_detected: true,
                },
            ]);
        });

        es.addEventListener("error", (e) => {
            try {
                const err = JSON.parse((e as MessageEvent).data);
                setLogs((prev) => [
                    ...prev,
                    {
                        ...err,
                        step_type: "error",
                        status: "failed",
                        sequence: prev.length + 1,
                        entry_id: "",
                        step_name: err.step_type ?? "error",
                        decision_summary: err.error ?? "",
                        duration_ms: 0,
                        container_id: null,
                        document_id: err.document_id ?? null,
                    },
                ]);
            } catch {
                // SSE connection error (not a data event), ignore
            }
        });

        es.addEventListener("complete", (e) => {
            setSummary(JSON.parse((e as MessageEvent).data));
            setIsRunning(false);
            es.close();
        });

        es.onerror = () => {
            if (es.readyState === EventSource.CLOSED) {
                setIsRunning(false);
            }
        };
    }, []);

    const connectStepInspector = useCallback((emailId: string, token: string, body: object) => {
        // Close any existing connections
        eventSourceRef.current?.close();
        abortRef.current?.abort();

        setIsRunning(true);
        setStepEvents([]);

        const base = API_BASE.replace(/\/$/, "");
        const prefix = API_PREFIX.startsWith("/") ? API_PREFIX : `/${API_PREFIX}`;
        const url = `${base}${prefix}/admin/debug/pipeline-steps/${emailId}/run?token=${encodeURIComponent(token)}`;

        const controller = new AbortController();
        abortRef.current = controller;

        (async () => {
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify(body),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    const errText = await response.text();
                    setStepEvents((prev) => [
                        ...prev,
                        { event: "error", error: `HTTP ${response.status}: ${errText}` },
                    ]);
                    setIsRunning(false);
                    return;
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    setIsRunning(false);
                    return;
                }

                const decoder = new TextDecoder();
                let buffer = "";
                let currentEvent = "";
                let currentData = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() ?? "";

                    for (const line of lines) {
                        if (line.startsWith("event: ")) {
                            currentEvent = line.slice(7).trim();
                        } else if (line.startsWith("data: ")) {
                            currentData = line.slice(6);
                        } else if (line.trim() === "" && currentEvent && currentData) {
                            try {
                                const parsed = JSON.parse(currentData);
                                const eventName = currentEvent;
                                setStepEvents((prev) => [
                                    ...prev,
                                    { ...parsed, event: eventName },
                                ]);

                                if (currentEvent === "run_complete") {
                                    setIsRunning(false);
                                }
                            } catch {
                                // ignore parse errors
                            }
                            currentEvent = "";
                            currentData = "";
                        }
                    }
                }

                setIsRunning(false);
            } catch (err) {
                if ((err as Error).name !== "AbortError") {
                    setStepEvents((prev) => [
                        ...prev,
                        { event: "error", error: (err as Error).message },
                    ]);
                }
                setIsRunning(false);
            }
        })();
    }, []);

    const clearStepEvents = useCallback(() => {
        setStepEvents([]);
    }, []);

    const disconnect = useCallback(() => {
        eventSourceRef.current?.close();
        abortRef.current?.abort();
        setIsRunning(false);
        setStepEvents([]);
    }, []);

    useEffect(() => {
        return () => {
            eventSourceRef.current?.close();
            abortRef.current?.abort();
        };
    }, []);

    return { logs, progress, summary, isRunning, connect, connectStepInspector, stepEvents, clearStepEvents, disconnect };
}
